import React, { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { ChevronDown, ChevronUp, Star } from "lucide-react";

// Shared section wrapper with collapse-toggle
function Section({ title, children, defaultOpen = true, testid }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="py-5 border-b border-[hsl(var(--kb-border))]" data-testid={testid}>
      <button
        className="w-full flex items-center justify-between text-left"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        <span className="overline">{title}</span>
        {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
      </button>
      {open && <div className="mt-3">{children}</div>}
    </div>
  );
}

function CountRow({ label, count, active, onToggle, testid }) {
  return (
    <label
      className={`flex items-center justify-between gap-2 text-sm py-1 cursor-pointer ${
        active ? "text-[hsl(var(--kb-primary))] font-medium" : ""
      }`}
      data-testid={testid}
    >
      <span className="flex items-center gap-2 flex-1 min-w-0">
        <input
          type="checkbox"
          className="shrink-0"
          checked={!!active}
          onChange={onToggle}
          aria-label={label}
        />
        <span className="truncate">{label}</span>
      </span>
      {count !== undefined && (
        <span className="text-xs text-[hsl(var(--kb-muted))] tabular-nums">{count}</span>
      )}
    </label>
  );
}

function ColorSwatch({ value, label, hex, count, selected, onToggle }) {
  const bg = hex && /^#[0-9A-Fa-f]{6}$/.test(hex) ? hex : "hsl(var(--kb-surface-alt))";
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-label={`${label}${count ? ` (${count})` : ""}`}
      aria-pressed={selected}
      title={`${label}${count ? ` · ${count}` : ""}`}
      data-testid={`filter-color-${value}`}
      className={`w-9 h-9 rounded-full border transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--kb-primary))] ${
        selected
          ? "ring-2 ring-offset-2 ring-[hsl(var(--kb-primary))] border-transparent"
          : "border-[hsl(var(--kb-border))] hover:scale-110"
      }`}
      style={{ background: bg }}
    />
  );
}

const DISCOUNT_TIERS = [10, 20, 30, 50];
const RATING_TIERS = [4, 3, 2];

/**
 * Props:
 *   filters      object of current filter values (multi-valued arrays for multi-select keys)
 *   setFilters   (updater|next) => void
 *   categories   flat top-level categories (legacy)
 *   facets       server-computed facet counts { colors, fabrics, occasions, work_types, sizes, sellers, ratings, in_stock, price_range, discount_buckets }
 */
export default function Filters({ filters, setFilters, categories = [], facets = {} }) {
  const [attrs, setAttrs] = useState([]);

  useEffect(() => {
    let alive = true;
    const params = filters.category ? `?category_slug=${filters.category}` : "";
    api
      .get(`/catalog/attributes${params}`)
      .then(({ data }) => alive && setAttrs(data.attributes || []))
      .catch(() => alive && setAttrs([]));
    return () => { alive = false; };
  }, [filters.category]);

  const setCategory = (slug) =>
    setFilters((f) => ({ ...f, category: f.category === slug ? undefined : slug }));

  const toggleMulti = (key, value) =>
    setFilters((f) => {
      const cur = Array.isArray(f[key]) ? f[key] : [];
      const next = cur.includes(value) ? cur.filter((v) => v !== value) : [...cur, value];
      return { ...f, [key]: next.length ? next : undefined };
    });

  const setSingle = (key, value) =>
    setFilters((f) => ({ ...f, [key]: f[key] === value ? undefined : value }));

  const toggleBool = (key) =>
    setFilters((f) => ({ ...f, [key]: f[key] ? undefined : true }));

  // Merge attribute values with backend counts for color/fabric/occasion/work_type/sizes
  const countMap = (facetArr) => {
    const m = {};
    (facetArr || []).forEach((it) => { if (it.value) m[String(it.value).toLowerCase()] = it.count; });
    return m;
  };
  const counts = useMemo(() => ({
    color: countMap(facets.colors),
    fabric: countMap(facets.fabrics),
    occasion: countMap(facets.occasions),
    work_type: countMap(facets.work_types),
    size: countMap(facets.sizes),
  }), [facets]);

  return (
    <div data-testid="filters-panel">
      <Section title="Category" testid="filter-cat-section">
        <div className="flex flex-col gap-2 text-sm">
          <button
            className={`text-left hover:text-[hsl(var(--kb-primary))] ${
              !filters.category ? "text-[hsl(var(--kb-primary))] font-medium" : ""
            }`}
            onClick={() => setFilters((f) => ({ ...f, category: undefined }))}
            data-testid="filter-category-all"
          >
            All
          </button>
          {categories.map((c) => (
            <button
              key={c.slug}
              onClick={() => setCategory(c.slug)}
              data-testid={`filter-category-${c.slug}`}
              className={`text-left hover:text-[hsl(var(--kb-primary))] ${
                filters.category === c.slug ? "text-[hsl(var(--kb-primary))] font-medium" : ""
              }`}
            >
              {c.name}
            </button>
          ))}
        </div>
      </Section>

      <Section title="Price" testid="filter-price-section">
        <div className="flex gap-2">
          <input
            type="number"
            placeholder={facets.price_range?.min ? `Min ${Math.round(facets.price_range.min)}` : "Min"}
            value={filters.min_price ?? ""}
            onChange={(e) =>
              setFilters((f) => ({
                ...f,
                min_price: e.target.value ? Number(e.target.value) : undefined,
              }))
            }
            className="kb-input flex-1"
            min={0}
            data-testid="filter-min-price"
          />
          <input
            type="number"
            placeholder={facets.price_range?.max ? `Max ${Math.round(facets.price_range.max)}` : "Max"}
            value={filters.max_price ?? ""}
            onChange={(e) =>
              setFilters((f) => ({
                ...f,
                max_price: e.target.value ? Number(e.target.value) : undefined,
              }))
            }
            className="kb-input flex-1"
            min={0}
            data-testid="filter-max-price"
          />
        </div>
      </Section>

      <Section title="Discount" testid="filter-discount-section">
        <div className="flex flex-col gap-1">
          {DISCOUNT_TIERS.map((d) => (
            <CountRow
              key={d}
              label={`${d}% or more`}
              active={Number(filters.discount_min) === d}
              onToggle={() => setSingle("discount_min", d)}
              testid={`filter-discount-${d}`}
            />
          ))}
        </div>
      </Section>

      <Section title="Availability" testid="filter-availability-section">
        <div className="space-y-1">
          <CountRow
            label="In stock only"
            count={facets.in_stock}
            active={!!filters.in_stock}
            onToggle={() => toggleBool("in_stock")}
            testid="filter-in-stock"
          />
          <CountRow
            label="Ready to ship"
            count={facets.ready_to_ship}
            active={!!filters.ready_to_ship}
            onToggle={() => toggleBool("ready_to_ship")}
            testid="filter-ready-to-ship"
          />
        </div>
      </Section>

      <Section title="Rating" testid="filter-rating-section">
        <div className="flex flex-col gap-1">
          {RATING_TIERS.map((r) => (
            <label
              key={r}
              className={`flex items-center gap-2 text-sm py-1 cursor-pointer ${
                Number(filters.min_rating) === r ? "text-[hsl(var(--kb-primary))] font-medium" : ""
              }`}
              data-testid={`filter-rating-${r}`}
            >
              <input
                type="checkbox"
                checked={Number(filters.min_rating) === r}
                onChange={() => setSingle("min_rating", r)}
              />
              <span className="flex items-center gap-0.5">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star
                    key={i}
                    size={12}
                    className={i < r ? "fill-[hsl(var(--kb-accent))] text-[hsl(var(--kb-accent))]" : "text-[hsl(var(--kb-border))]"}
                  />
                ))}
              </span>
              <span>& up</span>
            </label>
          ))}
        </div>
      </Section>

      {facets.sellers?.length > 0 && (
        <Section title="Seller" testid="filter-seller-section">
          <div className="max-h-48 overflow-y-auto pr-1 space-y-0.5">
            {facets.sellers.map((s) => (
              <CountRow
                key={s.id}
                label={s.shop_name}
                count={s.count}
                active={Array.isArray(filters.sellers) && filters.sellers.includes(s.id)}
                onToggle={() => toggleMulti("sellers", s.id)}
                testid={`filter-seller-${s.id}`}
              />
            ))}
          </div>
        </Section>
      )}

      {attrs.map((a) => {
        const selected = Array.isArray(filters[a.slug]) ? filters[a.slug] : [];
        const facetCounts = counts[a.slug] || {};
        if (a.input_type === "toggle" || a.data_type === "boolean") {
          return (
            <Section title={a.display_name} key={a.slug} testid={`filter-${a.slug}-section`}>
              <label className="flex items-center gap-2 cursor-pointer text-sm">
                <input
                  type="checkbox"
                  checked={!!filters[a.slug]}
                  onChange={() => toggleBool(a.slug)}
                  data-testid={`filter-toggle-${a.slug}`}
                />
                Only show {a.display_name}
              </label>
            </Section>
          );
        }
        if (a.input_type === "swatch" || a.slug === "color") {
          return (
            <Section title={a.display_name} key={a.slug} testid={`filter-${a.slug}-section`}>
              <div className="flex flex-wrap gap-3">
                {a.values.map((v) => (
                  <ColorSwatch
                    key={v.slug}
                    value={v.slug}
                    label={v.display_value}
                    hex={v.hex_color}
                    count={facetCounts[v.display_value.toLowerCase()]}
                    selected={selected.includes(v.display_value)}
                    onToggle={() => toggleMulti(a.slug, v.display_value)}
                  />
                ))}
              </div>
              {selected.length > 0 && (
                <div className="mt-3 text-xs text-[hsl(var(--kb-muted))]">
                  Selected: {selected.join(", ")}
                </div>
              )}
            </Section>
          );
        }
        return (
          <Section title={a.display_name} key={a.slug} testid={`filter-${a.slug}-section`}>
            <div className="flex flex-col gap-0.5">
              {a.values.map((v) => (
                <CountRow
                  key={v.slug}
                  label={v.display_value}
                  count={facetCounts[v.display_value.toLowerCase()]}
                  active={selected.includes(v.display_value)}
                  onToggle={() => toggleMulti(a.slug, v.display_value)}
                  testid={`filter-${a.slug}-${v.slug}`}
                />
              ))}
            </div>
          </Section>
        );
      })}

      <button
        onClick={() => setFilters({})}
        className="mt-4 text-xs uppercase tracking-wider underline"
        data-testid="filters-reset"
      >
        Clear all filters
      </button>
    </div>
  );
}
